import AppShell from "../components/layout/AppShell";
import AppFooter from "../components/layout/AppFooter";
import TopNav from "../components/layout/TopNav";

import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

export default function ClientAccount({ onLogout, onNavigate }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // Infos perso
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [infoSaving, setInfoSaving] = useState(false);
    const [infoMsg, setInfoMsg] = useState(null);

    // Mot de passe
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [pwdSaving, setPwdSaving] = useState(false);
    const [pwdMsg, setPwdMsg] = useState(null);
    const [phone, setPhone] = useState("");

    const getHeaders = () => {
        const token = localStorage.getItem("token");
        return { Authorization: `Bearer ${token}` };
    };

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await axios.get(`${API}/users/me`, { headers: getHeaders() });
                setProfile(res.data);
                setFirstName(res.data.first_name || "");
                setLastName(res.data.last_name || "");
                setPhone(res.data.phone || "");
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, []);

    const saveInfo = async (e) => {
        e.preventDefault();
        setInfoSaving(true);
        setInfoMsg(null);
        try {
            await axios.put(`${API}/users/me`, { first_name: firstName, last_name: lastName, phone }, { headers: getHeaders() });
            setInfoMsg({ type: "ok", text: "Informations mises à jour." });
        } catch {
            setInfoMsg({ type: "err", text: "Erreur lors de la mise à jour." });
        } finally {
            setInfoSaving(false);
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        if (newPwd !== confirmPwd) {
            setPwdMsg({ type: "err", text: "Les mots de passe ne correspondent pas." });
            return;
        }
        if (newPwd.length < 8) {
            setPwdMsg({ type: "err", text: "Le mot de passe doit faire au moins 8 caractères." });
            return;
        }
        setPwdSaving(true);
        setPwdMsg(null);
        try {
            await axios.put(`${API}/users/me/password`, {
                current_password: currentPwd,
                new_password: newPwd
            }, { headers: getHeaders() });
            setPwdMsg({ type: "ok", text: "Mot de passe modifié avec succès." });
            setCurrentPwd("");
            setNewPwd("");
            setConfirmPwd("");
        } catch (err) {
            setPwdMsg({ type: "err", text: err.response?.data?.error || "Erreur lors du changement." });
        } finally {
            setPwdSaving(false);
        }
    };

    const memberSince = profile?.created_at
        ? new Date(profile.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long" })
        : null;

    return (
        <AppShell
            header={<TopNav isLoggedIn={true} onLogout={onLogout} onNavigate={onNavigate} />}
            footer={<AppFooter onNavigate={onNavigate} />}
            onNavigate={onNavigate}
        >
            <div style={{ maxWidth: 700, margin: "0 auto" }}>
                <h2 className="page-title">Mon compte</h2>

                {loading ? (
                    <div className="section-card"><p className="muted">Chargement…</p></div>
                ) : (
                    <>
                        {/* Résumé */}
                        <div className="section-card" style={{ marginBottom: 14 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                <div style={{
                                    width: 52, height: 52, borderRadius: "50%",
                                    background: "rgba(110,86,207,0.18)",
                                    border: "1px solid rgba(110,86,207,0.35)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 22, fontWeight: 700, color: "#a78bfa"
                                }}>
                                    {(firstName?.[0] || profile?.email?.[0] || "?").toUpperCase()}
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                                        {firstName || lastName ? `${firstName} ${lastName}`.trim() : profile?.email}
                                    </div>
                                    <div className="muted" style={{ fontSize: 12 }}>{profile?.email}</div>
                                    {memberSince && <div className="muted" style={{ fontSize: 12 }}>Membre depuis {memberSince}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Infos perso */}
                        <div className="section-card" style={{ marginBottom: 14 }}>
                            <div className="section-head">
                                <h3 className="section-title">Informations personnelles</h3>
                            </div>
                            <form onSubmit={saveInfo} className="stack">
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                    <div>
                                        <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Prénom</label>
                                        <input
                                            className="input"
                                            value={firstName}
                                            onChange={e => setFirstName(e.target.value)}
                                            placeholder="Prénom"
                                        />
                                    </div>
                                    <div>
                                        <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Nom</label>
                                        <input
                                            className="input"
                                            value={lastName}
                                            onChange={e => setLastName(e.target.value)}
                                            placeholder="Nom"
                                        />
                                    </div>
                                    <div>
                                        <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Téléphone</label>
                                        <input
                                            className="input"
                                            value={phone}
                                            onChange={e => setPhone(e.target.value)}
                                            placeholder="+33 6 00 00 00 00"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Email</label>
                                    <input className="input" value={profile?.email || ""} disabled style={{ opacity: 0.5 }} />
                                </div>
                                {infoMsg && (
                                    <div style={{ fontSize: 13, color: infoMsg.type === "ok" ? "#4ade80" : "#f87171" }}>
                                        {infoMsg.type === "ok" ? "✓ " : "✕ "}{infoMsg.text}
                                    </div>
                                )}
                                <button type="submit" className="btn btn-primary" disabled={infoSaving}>
                                    {infoSaving ? "Sauvegarde…" : "Sauvegarder"}
                                </button>
                            </form>
                        </div>

                        {/* Mot de passe */}
                        <div className="section-card" style={{ marginBottom: 14 }}>
                            <div className="section-head">
                                <h3 className="section-title">Changer le mot de passe</h3>
                            </div>
                            <form onSubmit={savePassword} className="stack">
                                <div>
                                    <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Mot de passe actuel</label>
                                    <input
                                        className="input"
                                        type="password"
                                        value={currentPwd}
                                        onChange={e => setCurrentPwd(e.target.value)}
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                    <div>
                                        <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Nouveau mot de passe</label>
                                        <input
                                            className="input"
                                            type="password"
                                            value={newPwd}
                                            onChange={e => setNewPwd(e.target.value)}
                                            placeholder="••••••••"
                                        />
                                    </div>
                                    <div>
                                        <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Confirmer</label>
                                        <input
                                            className="input"
                                            type="password"
                                            value={confirmPwd}
                                            onChange={e => setConfirmPwd(e.target.value)}
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>
                                {pwdMsg && (
                                    <div style={{ fontSize: 13, color: pwdMsg.type === "ok" ? "#4ade80" : "#f87171" }}>
                                        {pwdMsg.type === "ok" ? "✓ " : "✕ "}{pwdMsg.text}
                                    </div>
                                )}
                                <button type="submit" className="btn btn-primary" disabled={pwdSaving || !currentPwd || !newPwd || !confirmPwd}>
                                    {pwdSaving ? "Modification…" : "Modifier le mot de passe"}
                                </button>
                            </form>
                        </div>

                        {/* Paiement */}
                        <div className="section-card">
                            <div className="section-head">
                                <h3 className="section-title">Paiement</h3>
                            </div>
                            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
                                Gérez vos moyens de paiement, rechargez votre wallet et consultez votre historique de transactions.
                            </p>
                            <button className="btn btn-primary" onClick={() => onNavigate("payment-methods")}>
                                Gérer mes paiements →
                            </button>
                        </div>
                    </>
                )}
            </div>
        </AppShell>
    );
}